import { changePct, formatMoney } from '@bqcrm/shared';
import { Badge, Card, Group, Paper, Stack, Text, Tooltip } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { IconArrowDownRight, IconArrowUpRight } from '@tabler/icons-react';
import { type ReactNode, useState } from 'react';

/** Kwota skrócona na oś i etykiety: „1,2 mln zł”, „350 tys. zł”. */
export function moneyShort(gr: number): string {
  const zl = gr / 100;
  const abs = Math.abs(zl);
  const nf = (v: number, d: number) => new Intl.NumberFormat('pl-PL', { maximumFractionDigits: d }).format(v);
  if (abs >= 1_000_000) return `${nf(zl / 1_000_000, 1)} mln zł`;
  if (abs >= 10_000) return `${nf(zl / 1000, 0)} tys. zł`;
  if (abs >= 1000) return `${nf(zl / 1000, 1)} tys. zł`;
  return `${nf(zl, 0)} zł`;
}

/** „Ładna” wartość szczytu osi (1, 2, 2,5, 5 × 10^n). */
function niceMax(v: number): number {
  if (v <= 0) return 1;
  const exp = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 2.5, 5, 10]) if (v <= m * exp) return m * exp;
  return 10 * exp;
}

/** Kafelek liczby z porównaniem do poprzedniego okresu. Kolor strzałki: wzrost = dobrze (chyba że `lowerIsBetter`). */
export function StatTile({ label, value, previous, current, hint, lowerIsBetter, unit = 'zł', footer }: { label: string; value: ReactNode; current?: number | null; previous?: number | null; hint?: string; lowerIsBetter?: boolean; unit?: 'zł' | 'pkt' | 'szt'; footer?: ReactNode }) {
  let delta: ReactNode = null;
  if (current !== undefined && current !== null && previous !== undefined && previous !== null) {
    // skuteczność: zmiana w punktach procentowych, nie w procentach
    const diff = unit === 'pkt' ? Math.round((current - previous) * 10) / 10 : changePct(current, previous);
    if (diff !== null && diff !== 0) {
      const up = diff > 0;
      const good = lowerIsBetter ? !up : up;
      delta = (
        <Badge variant="light" color={good ? 'eko' : 'red'} size="sm" leftSection={up ? <IconArrowUpRight size={12} /> : <IconArrowDownRight size={12} />}>
          {up ? '+' : ''}
          {new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 1 }).format(diff)}
          {unit === 'pkt' ? ' pkt' : '%'}
        </Badge>
      );
    } else if (diff === 0) {
      delta = (
        <Badge variant="light" color="gray" size="sm">
          bez zmian
        </Badge>
      );
    }
  }
  return (
    <Card withBorder padding="md">
      <Text size="xs" c="dimmed" fw={600} tt="uppercase" lh={1.3}>
        {label}
      </Text>
      <Text fz={24} fw={700} mt={6} lh={1.2} className="tabular" style={{ wordBreak: 'break-word' }}>
        {value}
      </Text>
      {(delta || hint) && (
        <Group gap={6} mt={6} wrap="wrap">
          {delta}
          {hint && (
            <Text size="xs" c="dimmed">
              {hint}
            </Text>
          )}
        </Group>
      )}
      {footer}
    </Card>
  );
}

export interface Series {
  key: string;
  label: string;
  /** zmienna CSS koloru, np. var(--chart-1) */
  color: string;
}

/** Słupki miesięczne (jedna oś, kwoty): 1–2 serie obok siebie, podpowiedź po najechaniu na miesiąc, legenda dla ≥ 2 serii. */
export function MonthBars({ data, series, height = 220, label }: { data: { label: string; values: Record<string, number> }[]; series: Series[]; height?: number; label: string }) {
  const { ref, width } = useElementSize();
  const [hover, setHover] = useState<number | null>(null);
  const padL = 64;
  const padB = 24;
  const padT = 8;
  const w = Math.max(width, 200);
  const plotW = w - padL - 8;
  const plotH = height - padB - padT;
  // podziałka w okrągłych krokach (1 / 2 / 2,5 / 5 × 10^n), ok. 4 linie
  const rawMax = Math.max(0, ...data.flatMap((d) => series.map((s) => d.values[s.key] ?? 0)));
  const rawMin = Math.min(0, ...data.flatMap((d) => series.map((s) => d.values[s.key] ?? 0)));
  const step = niceMax((rawMax - rawMin) / 4 || 100);
  const max = Math.max(step, Math.ceil(rawMax / step) * step);
  const min = Math.floor(rawMin / step) * step;
  const range = max - min || 1;
  const y = (v: number) => padT + plotH - ((v - min) / range) * plotH;
  const band = plotW / Math.max(data.length, 1);
  const gap = 2;
  const barW = Math.max(2, Math.min(28, (band * 0.7 - gap * (series.length - 1)) / series.length));
  const groupW = barW * series.length + gap * (series.length - 1);
  const ticks = Array.from({ length: Math.round(range / step) + 1 }, (_, i) => min + step * i);
  // co która etykieta miesiąca, żeby się nie nakładały
  const every = Math.max(1, Math.ceil(data.length / Math.max(1, Math.floor(plotW / 48))));
  const r = Math.min(4, barW / 2);
  const barPath = (x: number, v: number) => {
    const top = y(Math.max(v, 0));
    const bottom = y(Math.min(v, 0));
    const h = Math.max(bottom - top, v === 0 ? 0 : 1);
    if (h <= r || v < 0) return `M${x},${top}h${barW}v${h}h${-barW}z`;
    // zaokrąglony koniec danych, prosta podstawa przy osi
    return `M${x},${bottom}V${top + r}q0,${-r} ${r},${-r}h${barW - 2 * r}q${r},0 ${r},${r}V${bottom}z`;
  };

  return (
    <div>
      {series.length > 1 && (
        <Group gap="md" mb={6}>
          {series.map((s) => (
            <Group key={s.key} gap={6}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: s.color, display: 'inline-block' }} />
              <Text size="xs" c="dimmed">
                {s.label}
              </Text>
            </Group>
          ))}
        </Group>
      )}
      <div ref={ref} style={{ position: 'relative', width: '100%' }}>
        {width > 0 && (
          <svg width={w} height={height} role="img" aria-label={label} style={{ display: 'block' }} onMouseLeave={() => setHover(null)}>
            {ticks.map((t) => (
              <g key={t}>
                <line x1={padL} x2={w - 8} y1={y(t)} y2={y(t)} stroke="var(--chart-grid)" strokeWidth={1} />
                <text x={padL - 6} y={y(t) + 4} textAnchor="end" fontSize={11} fill="var(--mantine-color-dimmed)">
                  {moneyShort(t)}
                </text>
              </g>
            ))}
            {data.map((d, i) => {
              const x0 = padL + band * i + (band - groupW) / 2;
              return (
                <g key={d.label}>
                  {hover === i && <rect x={padL + band * i} y={padT} width={band} height={plotH} fill="var(--app-muted)" />}
                  {series.map((s, j) => (
                    <path key={s.key} d={barPath(x0 + j * (barW + gap), d.values[s.key] ?? 0)} fill={s.color} />
                  ))}
                  {i % every === 0 && (
                    <text x={padL + band * i + band / 2} y={height - 6} textAnchor="middle" fontSize={11} fill="var(--mantine-color-dimmed)">
                      {d.label}
                    </text>
                  )}
                  {/* obszar najechania większy niż słupek */}
                  <rect x={padL + band * i} y={padT} width={band} height={plotH} fill="transparent" onMouseEnter={() => setHover(i)} onClick={() => setHover(i)} />
                </g>
              );
            })}
            <line x1={padL} x2={w - 8} y1={y(0)} y2={y(0)} stroke="var(--app-line-strong)" strokeWidth={1} />
          </svg>
        )}
        {hover !== null && data[hover] && (
          <Paper
            withBorder
            shadow="sm"
            p="xs"
            style={{
              position: 'absolute',
              top: 0,
              left: Math.min(Math.max(padL + band * hover + band / 2 - 115, 0), Math.max(w - 230, 0)),
              width: 230,
              pointerEvents: 'none',
              zIndex: 2,
            }}
          >
            <Text size="xs" fw={700} mb={4}>
              {data[hover].label}
            </Text>
            {series.map((s) => (
              <Group key={s.key} justify="space-between" gap={6} wrap="nowrap">
                <Group gap={6} wrap="nowrap">
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color, display: 'inline-block', flexShrink: 0 }} />
                  <Text size="xs" c="dimmed">
                    {s.label}
                  </Text>
                </Group>
                <Text size="xs" fw={600} className="tabular">
                  {formatMoney(data[hover]!.values[s.key] ?? 0)}
                </Text>
              </Group>
            ))}
          </Paper>
        )}
      </div>
    </div>
  );
}

/** Poziome paski (lejek, źródła, wiek należności): etykieta, pasek proporcjonalny do największej wartości, wartość tekstem. */
export function HBars({ rows, color = 'var(--chart-1)' }: { rows: { key: string; label: ReactNode; value: number; text: ReactNode; sub?: ReactNode; color?: string; tooltip?: string }[]; color?: string }) {
  const max = Math.max(0, ...rows.map((r) => r.value)) || 1;
  return (
    <Stack gap={10}>
      {rows.map((r) => {
        const bar = (
          <div style={{ height: 10, borderRadius: 4, background: 'var(--app-muted)', overflow: 'hidden' }}>
            <div style={{ width: `${Math.max(0, (r.value / max) * 100)}%`, minWidth: r.value > 0 ? 3 : 0, height: '100%', borderRadius: 4, background: r.color ?? color }} />
          </div>
        );
        return (
          <div key={r.key}>
            <Group justify="space-between" gap="xs" wrap="nowrap" mb={4}>
              <Text size="sm" style={{ minWidth: 0 }} truncate>
                {r.label}
              </Text>
              <Text size="sm" fw={600} className="tabular" style={{ flexShrink: 0 }}>
                {r.text}
              </Text>
            </Group>
            {r.tooltip ? (
              <Tooltip label={r.tooltip} withArrow>
                {bar}
              </Tooltip>
            ) : (
              bar
            )}
            {r.sub && (
              <Text size="xs" c="dimmed" mt={2}>
                {r.sub}
              </Text>
            )}
          </div>
        );
      })}
    </Stack>
  );
}

export const pctText = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `${new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 1 }).format(v)}%`);
